import React, { useState, useRef, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import { Bell, BellFill, Check2All } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { AppNotification } from '../types';

const NotificationBell: React.FC = () => {
  const { unreadCount, notifications, fetchNotifications, markRead, markAllRead } = useNotifications();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = async () => {
    const next = !isOpen;
    if (next) {
      try {
        await fetchNotifications();
      } catch {
        // Notifications failed to load; still open dropdown to show empty state
      }
    }
    setIsOpen(next);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.read) {
      await markRead(notification.id);
    }
    setIsOpen(false);

    if (notification.resourceType === 'PROJECT' && notification.resourceId) {
      history.push(`/project/${notification.resourceId}`);
    } else if (notification.resourceType === 'GENERATION_JOB') {
      history.push('/dashboard');
    } else if (notification.resourceType === 'TEST_SUITE' && notification.resourceId) {
      history.push(`/suite/${notification.resourceId}`);
    } else if (notification.resourceType === 'TEST_RUN' && notification.resourceId) {
      history.push(`/runs/${notification.resourceId}/results`);
    }
  };

  const getTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'JOB_STARTED': return '#667eea';
      case 'JOB_COMPLETED': return '#22c55e';
      case 'JOB_FAILED': return '#ef4444';
      default: return '#667eea';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'JOB_STARTED': return '\u25B6';
      case 'JOB_COMPLETED': return '\u2713';
      case 'JOB_FAILED': return '\u2717';
      default: return '\u25CF';
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          padding: '6px 8px',
          color: '#333',
        }}
        aria-label="Notifications"
      >
        {unreadCount > 0 ? <BellFill size={18} /> : <Bell size={18} />}
        {unreadCount > 0 && (
          <Badge
            bg="danger"
            pill
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: 10,
              minWidth: 16,
              lineHeight: '16px',
              padding: '0 4px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            width: 360,
            maxHeight: 420,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: '1px solid #e9ecef',
            }}
          >
            <strong style={{ fontSize: 15 }}>Notifications</strong>
            {unreadCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); markAllRead(); }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Check2All size={14} /> Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 14 }}>
              No notifications yet
            </div>
          ) : (
            notifications.slice(0, 20).map(n => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                style={{
                  padding: '10px 16px',
                  background: n.read ? 'transparent' : '#f0f4ff',
                  borderBottom: '1px solid #f1f3f5',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = n.read ? '#f8f9fa' : '#e8edff'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = n.read ? 'transparent' : '#f0f4ff'; }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: getNotificationColor(n.type),
                      flexShrink: 0,
                      width: 24,
                      textAlign: 'center',
                      lineHeight: '24px',
                    }}
                  >
                    {getNotificationIcon(n.type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: n.read ? 400 : 600, fontSize: 14 }}>{n.title}</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2, lineHeight: 1.4 }}>{n.message}</div>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{getTimeAgo(n.createdAt)}</div>
                  </div>
                  {!n.read && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#667eea', flexShrink: 0, marginTop: 8,
                    }} />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
